import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { useGetBlogMetaQuery } from "../../services/rtkApi/blogApi";

/**
 * Can the current user author blog posts?
 *
 * The JWT `role` claim is the fast path, but it is not the authority. The
 * backend resolves the role from the token *and* falls back to `User.role` in
 * Mongo (see `middleware/canWriteBlog.ts`), so someone granted `writer` in the
 * backend user record is allowed by the API while their access token still says
 * `member`. Reading only the claim would show them a locked door the server
 * would have opened.
 *
 * So: trust the claim when it already says writer, and otherwise ask the server
 * — `/api/blog/meta` sits behind the same gate and 403s for everyone else. The
 * probe is skipped for logged-out visitors and for anyone the claim already
 * covers, so the common paths cost nothing.
 */
export function isWriterRole(role: string | null | undefined): boolean {
  return checkRole(role ?? "", "admin") || checkRole(role ?? "", "writer");
}

export type WriterCheck = {
  isWriter: boolean;
  /** true while the session or the server probe is still resolving */
  isChecking: boolean;
  isAdmin: boolean;
};

export function useIsWriter(): WriterCheck {
  const { token, role, bootstrapStatus } = useAppSelector((state) => state.auth);
  const sessionReady = bootstrapStatus === "ready";
  const claimSaysWriter = isWriterRole(role);

  const { data, isLoading, isError } = useGetBlogMetaQuery(undefined, {
    skip: !sessionReady || !token || claimSaysWriter,
  });

  const serverSaysWriter = !!data && !isError;

  return {
    isWriter: !!token && (claimSaysWriter || serverSaysWriter),
    isChecking:
      !sessionReady || (!!token && !claimSaysWriter && isLoading),
    isAdmin: checkRole(role ?? "", "admin") || !!data?.isAdmin,
  };
}
